import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Heading,
  Text,
  SimpleGrid,
  Badge,
  Flex,
  HStack,
  Icon,
  Card,
  CardBody,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { useParams, useLocation, Link as RouterLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getFleetDetails,
  getFleetTrips,
  getFleetDriverHistory,
  getFleetAuditLogs,
} from "../../../actions/inventory/fleet.action";
import { DocumentCard } from "../../../components/DocumentImageViewer";
import { Loading } from "../../../components/Loading";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";

const FleetTrips = ({ fleetId }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await getFleetTrips(fleetId);
        setTrips(res?.trips || res?.data?.trips || []);
      } catch (err) {
        console.error("Failed to fetch trips", err);
      } finally {
        setLoading(false);
      }
    };
    if (fleetId) fetchTrips();
  }, [fleetId]);

  if (loading) return <Spinner />;

  if (trips.length === 0) {
    return <Text color="gray.500">No trips found for this vehicle.</Text>;
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Trip Code</Th>
            <Th>Booking Status</Th>
            <Th>Trip Status</Th>
            <Th>Driver</Th>
            <Th>Vendor</Th>
            <Th>Date</Th>
          </Tr>
        </Thead>
        <Tbody>
          {trips.map((trip) => (
            <Tr key={trip._id} _hover={{ bg: "gray.50" }}>
              <Td>
                <Text
                  as={RouterLink}
                  to={`/bookings/${trip.bookingId?._id || trip.bookingId || trip._id}`}
                  color="blue.500"
                  fontWeight="medium"
                >
                  {trip.tripCode || trip._id.substring(0, 8)}
                </Text>
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    trip.bookingStatus === "completed"
                      ? "green"
                      : trip.bookingStatus === "cancelled"
                        ? "red"
                        : "yellow"
                  }
                >
                  {trip.bookingStatus || "N/A"}
                </Badge>
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    trip.tripStatus === "completed"
                      ? "green"
                      : trip.tripStatus === "in_progress"
                        ? "blue"
                        : "gray"
                  }
                >
                  {trip.tripStatus || "N/A"}
                </Badge>
              </Td>
              <Td>{trip.driverId?.name || "-"}</Td>
              <Td>{trip.vendorId?.vendorName || "-"}</Td>
              <Td>{new Date(trip.createdAt).toLocaleDateString()}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const FleetDriverHistory = ({ fleetId }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await getFleetDriverHistory(fleetId);
        setDrivers(res?.data || []);
      } catch (err) {
        console.error("Failed to fetch driver history", err);
      } finally {
        setLoading(false);
      }
    };
    if (fleetId) fetchDrivers();
  }, [fleetId]);

  if (loading) return <Spinner />;

  if (drivers.length === 0) {
    return <Text color="gray.500">No drivers assigned to this vehicle yet.</Text>;
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Driver Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {drivers.map((record) => (
            <Tr key={record.driver._id} _hover={{ bg: "gray.50" }}>
              <Td>
                <Text
                  as={RouterLink}
                  to={`/fleet-management/driver/${record.driver._id}`}
                  color="blue.500"
                  fontWeight="medium"
                >
                  {record.driver.name || `${record.driver.firstName || ""} ${record.driver.lastName || ""}`.trim() || "-"}
                </Text>
              </Td>
              <Td>{record.driver.contactDetails?.email || record.driver.email || "-"}</Td>
              <Td>{record.driver.contactDetails?.mobileNumber || record.driver.phoneNumber || "-"}</Td>
              <Td>
                <Badge colorScheme={record.currentlyAssigned ? "green" : "gray"}>
                  {record.currentlyAssigned ? "Currently Assigned" : "Previously Assigned"}
                </Badge>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const FleetAuditLogs = ({ fleetId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await getFleetAuditLogs(fleetId);
        setLogs(res?.data || []);
      } catch (err) {
        console.error("Failed to fetch audit logs", err);
      } finally {
        setLoading(false);
      }
    };
    if (fleetId) fetchLogs();
  }, [fleetId]);

  if (loading) return <Spinner />;

  if (logs.length === 0) {
    return <Text color="gray.500">No audit logs found.</Text>;
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Date & Time</Th>
            <Th>Action</Th>
            <Th>Performed By</Th>
            <Th>Role</Th>
            <Th>Changes Summary</Th>
          </Tr>
        </Thead>
        <Tbody>
          {logs.map((log) => (
            <Tr key={log._id} _hover={{ bg: "gray.50" }}>
              <Td whiteSpace="nowrap">
                {new Date(log.createdAt).toLocaleString()}
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    log.actionType === "CREATE"
                      ? "green"
                      : log.actionType === "UPDATE"
                        ? "blue"
                        : "red"
                  }
                >
                  {log.actionType}
                </Badge>
              </Td>
              <Td>{log.performedByInfo?.name || "-"}</Td>
              <Td>{log.performedByInfo?.role || "-"}</Td>
              <Td maxW="300px">
                <Text noOfLines={2} title={log.changesDescription}>
                  {log.changesDescription || "No specific details"}
                </Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const FleetEach = () => {
  const { id } = useParams();
  const location = useLocation();
  const [fleet, setFleet] = useState(location?.state?.driverData || null);
  const [loading, setLoading] = useState(!location?.state?.driverData);

  const fetchFleetDetails = async () => {
    try {
      const res = await getFleetDetails(id);
      setFleet(res?.data?.data || res?.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const InfoItem = ({ label, value, icon }) => (
    <Box>
      <HStack spacing={2} mb={1}>
        {icon && <Icon as={icon} color="blue.500" boxSize={4} />}
        <Text fontSize="sm" color="gray.500" fontWeight="medium">
          {label}
        </Text>
      </HStack>
      <Text fontWeight="semibold" fontSize="md">
        {value || "-"}
      </Text>
    </Box>
  );

  const ImageCard = ({ label, fileName }) => {
    return (
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        p={3}
        w="100%"
      >
        <Text fontSize="sm" color="gray.500" mb={1}>
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          {fileName || "-"}
        </Text>
      </Box>
    );
  };

  const CertificateCard = ({ label, imageUrl }) => (
    <DocumentCard label={label} imageUrl={imageUrl} />
  );

  useEffect(() => {
    // Only fetch if data wasn't passed via location state
    if (!fleet && id) {
      fetchFleetDetails();
    }
  }, [id, fleet]);

  if (loading) {
    return <Loading />;
  }

  if (!fleet) {
    return (
      <Box textAlign="center" p={10}>
        <Text fontSize="xl" color="gray.600">
          No fleet data found
        </Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1200px" mx="auto" w="100%">
      {/* Breadcrumbs */}
      <BreadCrumbMaker
        items={[
          { title: "Fleet Management", link: "#" },
          { title: "Vehicles", link: "/fleet-management/vehicles" },
          {
            title: "View Vehicle",
            link: `/fleet-management/vehicles/${id}`,
            on: true,
          },
        ]}
      />

      <Tabs colorScheme="blue" variant="unstyled" mt={4}>
        <TabList>
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            mr={2}
            borderRadius="md"
          >
            Documents
          </Tab>
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            borderRadius="md"
            mr={2}
          >
            Trips
          </Tab>
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            mr={2}
            borderRadius="md"
          >
            Driver History
          </Tab>
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            borderRadius="md"
          >
            Audit Log
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" mb={6}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Heading size="sm" color="gray.700" mb={6}>
                  Vehicle Ownership Details
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem
                    label="Owner Name (RC)"
                    value={fleet.realOwnerName || fleet.ownerDetails?.name}
                  />
                  <InfoItem
                    label={fleet.ownerType === "vendor" ? "Vendor Name" : "Driver Name"}
                    value={fleet.ownerDetails?.name}
                  />
                  <InfoItem label="Ownership Type" value={fleet.ownerType} />

                  <Box>
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      fontWeight="medium"
                      mb={1}
                    >
                      Owner ID
                    </Text>
                    <Text fontWeight="semibold" fontSize="md">
                      {fleet.ownerDetails?.id ? (
                        <Text
                          as={RouterLink}
                          to={
                            fleet.ownerType === "driver"
                              ? `/fleet-management/driver/${fleet.ownerDetails.id}`
                              : `/fleet-management/vendor/${fleet.ownerDetails.id}`
                          }
                          color="blue.500"
                          textDecoration="underline"
                        >
                          {fleet.ownerDetails.id}
                        </Text>
                      ) : (
                        "-"
                      )}
                    </Text>
                  </Box>

                  <InfoItem
                    label="Email Id"
                    value={fleet.ownerDetails?.email}
                  />
                </SimpleGrid>

                <Flex gap={4} wrap="wrap">
                  {fleet.ownershipDetails?.ownerPhoto && (
                    <Box borderRadius="md" maxW="300px" flex={1}>
                      <CertificateCard
                        label="Owner Photo"
                        imageUrl={fleet.ownershipDetails?.ownerPhoto}
                      />
                    </Box>
                  )}
                  {fleet.ownershipDetails?.someoneElseVehicle && (
                    <Box borderRadius="md" maxW="300px" flex={1}>
                      <CertificateCard
                        label="Ownership Agreement"
                        imageUrl={fleet.ownershipDetails?.ownershipAgreement}
                      />
                    </Box>
                  )}
                </Flex>
              </CardBody>
            </Card>
            {/* Header */}
            {/* <Box mb={6} mt={6}>
        <Heading size="sm" mb={2} color="#1E2A5A">
          Booking / View Leads
        </Heading>
      </Box> */}

            <Card borderRadius="xl" boxShadow="sm" mb={6}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Heading size="sm" color="gray.700" mb={6}>
                  Vehicle Information
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem label="Vehicle Type" value={fleet.vehicleType} />
                  <InfoItem label="Model" value={fleet.model} />
                  <InfoItem label="Manufacturer" value={fleet.manufacturer} />
                  <InfoItem label="Year" value={fleet.year} />
                  <InfoItem label="Year" value={fleet.year || "2002"} />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem
                    label="Vehicle Number"
                    value={fleet.vehicleNumber}
                  />
                  <InfoItem label="Vehicle Color" value={fleet.vehicleColor} />
                  <InfoItem
                    label="Seating Capacity"
                    value={fleet.seatingCapacity}
                  />
                  <InfoItem label="Fuel Type" value={fleet.fuelType} />
                </SimpleGrid>

                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr 1fr 1fr" }}
                  gap={{ base: 4, md: 6 }}
                  alignItems="flex-start"
                >
                  <Box>
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      fontWeight="medium"
                      mb={1}
                    >
                      Registration Number
                    </Text>
                    <Text fontWeight="semibold" fontSize="md">
                      {fleet.registrationNumber || "-"}
                    </Text>
                  </Box>

                  <GridItem colSpan={{ base: 1, md: 3 }}>
                    <Flex gap={4}>
                      <Box w="50%">
                        <CertificateCard
                          label="Registration Front Image"
                          imageUrl={fleet.registrationFront}
                        />
                      </Box>

                      <Box w="50%">
                        <CertificateCard
                          label="Registration Back Image"
                          imageUrl={fleet.registrationBack}
                        />
                      </Box>
                    </Flex>
                  </GridItem>
                </Grid>
              </CardBody>
            </Card>
            <Card borderRadius="xl" boxShadow="sm" mb={6}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Heading size="sm" color="gray.700" mb={6}>
                  Insurance Details
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem
                    label="Insurance Provider"
                    value={fleet.insuranceProvider}
                  />
                  <InfoItem label="Policy No." value={fleet.policyNumber} />
                  <InfoItem
                    label="Insurance Expiry"
                    value={
                      fleet.insuranceExpiryDate
                        ? new Date(
                          fleet.insuranceExpiryDate,
                        ).toLocaleDateString()
                        : "-"
                    }
                  />
                  <InfoItem
                    label="Fitness End Date"
                    value={
                      fleet.fitnessEndDate
                        ? new Date(fleet.fitnessEndDate).toLocaleDateString()
                        : "-"
                    }
                  />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem
                    label="PUC End Date"
                    value={
                      fleet.pucEndDate
                        ? new Date(fleet.pucEndDate).toLocaleDateString()
                        : "-"
                    }
                  />
                  <InfoItem
                    label="Permit End Date"
                    value={
                      fleet.permitEndDate
                        ? new Date(fleet.permitEndDate).toLocaleDateString()
                        : "-"
                    }
                  />
                  <InfoItem
                    label="Tax Paid Till Date"
                    value={
                      fleet.taxPaidTillDate
                        ? new Date(fleet.taxPaidTillDate).toLocaleDateString()
                        : "-"
                    }
                  />
                  <Box />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 3 }}
                  spacing={{ base: 4, md: 6 }}
                >
                  <CertificateCard
                    label="Insurance Certificate"
                    imageUrl={fleet.insuranceImage}
                  />
                  <CertificateCard
                    label="Fitness Certificate"
                    imageUrl={fleet.fitnessCertificate}
                  />
                  <CertificateCard
                    label="PUC Certificate"
                    imageUrl={fleet.pucCertificate}
                  />
                  <CertificateCard
                    label="Permit Certificate"
                    imageUrl={fleet.permitCertificate}
                  />
                  <CertificateCard
                    label="Tax Document"
                    imageUrl={fleet.taxDocument}
                  />
                  <CertificateCard
                    label="Vehicle Image"
                    imageUrl={fleet.vehicleImage}
                  />
                </SimpleGrid>
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" w="full">
              <CardBody>
                <Heading size="sm" color="gray.700" mb={6}>
                  Assigned Trips
                </Heading>
                <FleetTrips fleetId={fleet._id || id} />
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" w="full">
              <CardBody>
                <Heading size="sm" color="gray.700" mb={6}>
                  Driver Assignment History
                </Heading>
                <FleetDriverHistory fleetId={fleet._id || id} />
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" w="full">
              <CardBody>
                <Heading size="sm" color="gray.700" mb={6}>
                  Audit Log
                </Heading>
                <FleetAuditLogs fleetId={fleet._id || id} />
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default FleetEach;
